import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Ban } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { messageDe, useChargement } from "@/lib/chargement";
import { quantiteLisible } from "@/lib/libelles";
import { api, type PropositionReception } from "@/lib/api";
import { FormulaireReception } from "@/pages/reception-nouvelle";

/**
 * La réception d'un BL déposé (LOT 2, ticket #24) : LABGEST a lu le BL et propose
 * la commande, par ligne ce que le BL annonce, et ce qu'il n'a pas su rapprocher
 * — contre les seules lignes que l'appelant peut constater. La logistique corrige,
 * confirme les manquants, et valide : la validation constate la réception
 * (ADR-0038). Un BL déposé par erreur se rejette, avec son motif.
 */
export function PageReceptionDuBl() {
  const { id = "" } = useParams();
  const { donnees, erreur, enCours, recharger } = useChargement(
    (signal) => api.propositionReception(id, signal),
    [id],
  );

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Lecture du BL…" />;
  if (donnees.statut !== "A_VALIDER") {
    return <Erreur message="Ce BL n'attend plus de validation." />;
  }
  return <ReceptionProposee proposition={donnees} />;
}

function ReceptionProposee({ proposition }: { proposition: PropositionReception }) {
  const [identifiants, setIdentifiants] = useState(proposition.commandes_fournisseurs);
  const initiales = Object.fromEntries(
    proposition.lignes.map((ligne) => [
      ligne.ligne_commande_fournisseur_id,
      { annoncee: String(ligne.quantite_annoncee), recue: String(ligne.quantite_proposee) },
    ]),
  );
  return (
    <FormulaireReception
      identifiants={identifiants}
      changerCommandes={setIdentifiants}
      initiales={initiales}
      numeroBlInitial={proposition.numero_bl ?? ""}
      depuisUnBl
      libelleEnvoi="Valider le BL et constater la réception"
      avant={<CeQueLeBlDit proposition={proposition} />}
      envoyer={async (saisie) =>
        (await api.validerBl(proposition.import_id, saisie)).receptions_logistiques
      }
    />
  );
}

function CeQueLeBlDit({ proposition }: { proposition: PropositionReception }) {
  const naviguer = useNavigate();
  const [motif, setMotif] = useState("");
  const [rejet, setRejet] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const rien = proposition.lignes.length === 0 && proposition.non_rapprochees.length === 0;
  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Ce que LABGEST a lu du BL</h2>
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-3 text-sm">
        {rien ? (
          <p className="text-encre">
            LABGEST n'a rien lu de ce BL : choisissez sa commande et saisissez ce qui est arrivé.
          </p>
        ) : (
          <p className="text-encre">
            {proposition.lignes.length > 1
              ? `${proposition.lignes.length} lignes rapprochées de vos commandes`
              : proposition.lignes.length === 1
                ? "1 ligne rapprochée de vos commandes"
                : "Aucune ligne rapprochée de vos commandes"}
            {" : "}le reçu proposé est ce que le BL annonce. Corrigez-le d'après le colis.
          </p>
        )}
        {proposition.non_rapprochees.length > 0 ? (
          <div>
            <p className="font-medium text-encre">Lignes du BL non rapprochées</p>
            <ul className="mt-1 list-disc pl-5 text-encre-douce">
              {proposition.non_rapprochees.map((ligne, rang) => (
                <li key={rang} className="break-words">
                  {ligne.reference ? `${ligne.reference} — ` : ""}
                  {ligne.designation} : {quantiteLisible(ligne.quantite)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {rejet ? (
          <form
            aria-label="Rejeter ce BL"
            className="flex flex-col gap-3"
            onSubmit={async (evenement) => {
              evenement.preventDefault();
              setRefus(null);
              try {
                await api.rejeterImportOcr(proposition.import_id, motif.trim());
                naviguer("/receptions");
              } catch (cause) {
                setRefus(messageDe(cause));
              }
            }}
          >
            <Champ
              libelle="Motif du rejet"
              required
              value={motif}
              onChange={(evenement) => setMotif(evenement.target.value)}
            />
            {refus ? <Erreur message={refus} /> : null}
            <div className="flex flex-wrap gap-2">
              <Bouton type="submit" variante="principal" disabled={!motif.trim()}>
                Rejeter le BL
              </Bouton>
              <Bouton type="button" variante="discret" onClick={() => setRejet(false)}>
                Garder le BL
              </Bouton>
            </div>
          </form>
        ) : (
          <div>
            <Bouton
              type="button"
              taille="compact"
              variante="discret"
              onClick={() => setRejet(true)}
            >
              <Ban className="h-4 w-4" aria-hidden />
              Rejeter ce BL
            </Bouton>
          </div>
        )}
      </CorpsCarte>
    </Carte>
  );
}
